import { el, toast } from '../../dom';
import { fieldApi, foodApi, BAG_FULL_MESSAGE, type Bag, type FoodStatus, type PoisonDto } from '../../game-api';
import { ApiError } from '../../../core/network/api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { closePlanter, openOverlay, refreshBag, type FarmCtx } from './context';
import { spawnAdjacentTile, armCampfireClick } from './scene';
import { createCampfire } from '../../map/campfire';
import { richText } from '../../ui/icons';
import { modalWrap } from '../../ui/panel';
import { itemIconSrc, CROP_IDS } from '../../ui/item-icons';

/**
 * Food & the campfire (Block 1.8, client half).
 *
 * The server owns the effect — how much a food heals, whether raw meat poisons,
 * how long cooking takes. This module is the surface: a panel to eat from the bag
 * and to run the campfire's cooking queue, plus a poison countdown that ticks the
 * server's per-second damage locally (client-simulated, like everything time
 * based — the worst a tampered client does is skip its own poison).
 */

/**
 * Everything the server's food.config FOODS will accept — mirrored here because
 * this list is what the panel OFFERS, and the panel is the only eat surface in
 * the game. The old hand-written table listed nine ids, so egg, milk, mushroom
 * and 25 of the 28 crops were edible on the server and unreachable for a player:
 * POST /food/eat worked, and nothing in the UI ever sent it.
 *
 * Names and icons are not carried here any more — `foodName` derives the label
 * and `itemIconSrc` already owns the art for every bag id. Duplicating the icon
 * URL is how the produce rows ended up pointing at raw 16×32 crop art instead of
 * the baked 32×32 icons.
 */
export const EDIBLE_IDS: string[] = [
  'raw_meat', 'raw_beef', 'raw_mutton',
  'cooked_meat', 'cooked_beef', 'cooked_mutton',
  // Animal & forage staples: the coop, the cow pen and the Forager villager all
  // pay these out, and until now the only thing you could do with them was sell.
  'egg', 'milk', 'mushroom',
  // Fishing. Raw fish is a gamble like raw meat (a smaller one — it is fresher);
  // the cooked one is the best energy per unit in the game, which is the whole
  // reason to stand on a bank. Mirrors FOODS in backend food.config.
  'fish_mudfish', 'fish_greyling', 'fish_reedfish', 'fish_bluefin',
  'fish_deepscale', 'fish_frostfin', 'fish_rubyscale', 'cooked_fish',
  ...CROP_IDS.map((c) => `produce_${c}`),
];

/**
 * Foods stored as a Farm COLUMN rather than a bag row (backend forager.config
 * FARM_COLUMN ∩ FOODS). They never appear in `bag.items`, so listing them off the
 * item stacks — which is all this panel ever did — could not surface a mushroom
 * even once it was edible. They come off `bag.resources` instead; the server's
 * eat() routes on the same distinction and spends the column with a guarded
 * updateMany, so the client still just posts the itemId.
 */
export const COLUMN_FOODS = new Set(['mushroom']);

/** Raw → what it becomes on the fire (mirror of the server's COOKS_INTO, for labels). */
export const COOKABLE = new Set(['raw_meat', 'raw_beef', 'raw_mutton']);

const EDIBLE_SET = new Set(EDIBLE_IDS);
export const isEdible = (itemId: string) => EDIBLE_SET.has(itemId);

export interface FoodRow { itemId: string; quantity: number; column: boolean }

/**
 * Everything the player can eat right now, from BOTH stores, raw meats first.
 *
 * Pure on purpose: the panel's whole "what is on offer" decision lives here, so a
 * test can assert an egg/milk/mushroom is offered without a DOM.
 */
export function edibleStacks(bag: {
  items: { itemId: string; quantity: number }[];
  resources?: Record<string, number>;
}): FoodRow[] {
  const rows: FoodRow[] = bag.items
    .filter((it) => isEdible(it.itemId) && !COLUMN_FOODS.has(it.itemId) && it.quantity > 0)
    .map((it) => ({ itemId: it.itemId, quantity: it.quantity, column: false }));
  for (const id of COLUMN_FOODS) {
    const qty = bag.resources?.[id] ?? 0;
    if (isEdible(id) && qty > 0) rows.push({ itemId: id, quantity: qty, column: true });
  }
  return rows.sort((a, b) => Number(COOKABLE.has(b.itemId)) - Number(COOKABLE.has(a.itemId)));
}

// ─── Poison countdown ─────────────────────────────────────────────────────────

export interface PoisonEffect {
  /** Seconds of poison left (0 = clear). */
  remaining(): number;
  active(): boolean;
  /** Advance by `dtMs`; whole HP points that have come due are handed to `onTick`. */
  update(dtMs: number): void;
  /** (Re)apply poison — resets the timer to the full duration. */
  apply(p: PoisonDto): void;
  clear(): void;
}

/**
 * Food poisoning: `hpPerSec` damage for `durationSec`, applied as whole points.
 *
 * Damage accrues continuously but only reports on integer boundaries, so a 1hp/s
 * poison delivers exactly one damage call per second rather than a fractional one
 * every frame. `onTick` is where the caller sends that damage to the server.
 */
export function createPoisonEffect(onTick: (hp: number) => void): PoisonEffect {
  let leftMs = 0;
  let rate = 0;          // hp per ms
  let accrued = 0;       // fractional hp built up since the last whole point
  return {
    remaining: () => leftMs / 1000,
    active: () => leftMs > 0,
    apply(p) { leftMs = p.durationSec * 1000; rate = p.hpPerSec / 1000; accrued = 0; },
    clear() { leftMs = 0; accrued = 0; },
    update(dtMs) {
      if (leftMs <= 0) return;
      const step = Math.min(dtMs, leftMs);
      leftMs -= step;
      accrued += step * rate;
      const whole = Math.floor(accrued);
      if (whole > 0) { accrued -= whole; onTick(whole); }
    },
  };
}

// ─── Cooking / eating panel (DOM) ─────────────────────────────────────────────

const FONT = 'font-family:"Terraboma","CuteFantasy",ui-sans-serif';
const pxImg = (src: string, cls = 'w-7 h-7') =>
  el('img', `${cls} object-contain pointer-events-none`, { src, style: 'image-rendering:pixelated' });

/**
 * Human label for a food id. Derived from the id rather than a second name table:
 * the old hand-written EDIBLE map WAS that table, and it is exactly what left egg,
 * milk, mushroom and 25 crops out of the panel when they were added server-side.
 */
export const foodName = (id: string) =>
  id.split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');
const label = foodName;

export function openFood(c: FarmCtx) {
  void dispatchAction('ui.food', async () => {
    const [bag, status] = await Promise.all([fieldApi.bag(), foodApi.status()]);
    renderFood(c, bag, status);
  }).catch((e) => { if (!isDropped(e)) toast('Could not open campfire', 'error'); });
}

function renderFood(c: FarmCtx, bag: Bag, st: FoodStatus) {
  const card = el('div', 'bg-surface border border-border rounded-lg p-4 w-[420px] max-w-[92vw] max-h-[86vh] overflow-y-auto shadow-lift sheet-card', {},
    el('div', 'flex justify-between items-center mb-3', {},
      el('h3', 'font-heading text-gold inline-flex items-center gap-1.5', {}, ...richText('🔥 Campfire')),
      el('button', 'tb-close-x text-text-dim hover:text-text text-lg', { onClick: () => closePlanter(c) }, '×')));

  // No campfire yet → offer to build one near spawn. Cooking still works (the
  // panel is the fire), but building places the visible prop and its lit zone.
  if (!st.campfire) {
    card.append(el('div', 'flex items-center gap-3 p-2 mb-3 rounded border border-gold/40 bg-gold/5', {},
      el('div', 'flex-1', {},
        el('div', 'text-sm text-gold', {}, 'Build a campfire'),
        el('div', 'text-[11px] text-text-dim font-mono inline-flex items-center gap-1 flex-wrap', {}, ...richText(`🪵 ${st.campfireCost.wood} · 🪨 ${st.campfireCost.stone} · keeps night mobs away`))),
      el('button', 'px-2.5 py-1 min-h-[44px] rounded border border-gold text-gold text-sm hover:bg-gold/10',
        { onClick: () => void doBuildCampfire(c) }, 'Build')));
  }

  // Eat / cook list
  const stacks = edibleStacks(bag);
  card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mb-1', {}, 'Food'));
  if (!stacks.length) {
    card.append(el('div', 'text-[12px] text-text-dim mb-2', {}, 'No food. Hunt animals for meat, or harvest crops.'));
  }
  for (const it of stacks) {
    const raw = COOKABLE.has(it.itemId);
    const row = el('div', 'flex items-center gap-3 p-2 mb-1.5 rounded border border-border', {},
      // The normalised 32x32 bake, not the raw source art: /sprites/crops art is
      // 16x32 with its content in the LOWER half, so hand-built URLs drew a carrot
      // at half the size of the meat row above it and jammed to the bottom edge.
      pxImg(itemIconSrc(it.itemId)),
      el('div', 'flex-1', {},
        el('div', 'text-sm', {}, foodName(it.itemId)),
        el('div', 'text-[11px] text-text-dim font-mono', {}, `×${it.quantity}${raw ? ' · may poison raw' : ''}`)),
      el('button', 'px-2.5 py-1 min-h-[44px] rounded border border-border text-sm hover:border-gold hover:text-gold',
        { onClick: () => void doEat(c, it.itemId) }, 'Eat'));
    if (raw)
      row.append(el('button', 'px-2.5 py-1 min-h-[44px] rounded border border-gold/60 text-gold text-sm hover:bg-gold/10',
        { onClick: () => void doCook(c, it.itemId) }, 'Cook'));
    card.append(row);
  }

  // Cooking queue
  card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mt-3 mb-1', {}, 'On the fire'));
  const anyReady = st.cooking.some((s) => s.ready);
  if (!st.cooking.length) {
    card.append(el('div', 'text-[12px] text-text-dim', {}, 'Nothing cooking.'));
  } else {
    for (const slot of st.cooking) {
      card.append(el('div', 'flex items-center gap-3 p-1.5 mb-1 rounded bg-surface-2', {},
        el('div', 'flex-1 text-[13px]', { style: FONT }, label(slot.cookedItem)),
        el('div', `text-[12px] font-mono ${slot.ready ? 'text-energy' : 'text-text-dim'}`, {},
          slot.ready ? 'ready' : fmtSecs(slot.readyInMs))));
    }
    const collect = el('button',
      `w-full mt-2 px-3 py-2 rounded border text-sm ${anyReady ? 'border-energy text-energy hover:bg-energy/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
      { onClick: anyReady ? () => void doCollect(c) : undefined }, 'Collect ready');
    card.append(collect);
  }

  // openOverlay, never a bare appendChild: it is what attaches the Escape handler,
  // so the campfire panel closes like every other farm panel. z-[60] keeps its ×
  // above the immersive floating cluster (z-56) instead of underneath it.
  openOverlay(c, modalWrap(() => closePlanter(c), card, 'z-[60]'));
}

const fmtSecs = (ms: number) => `${Math.ceil(ms / 1000)}s`;

async function doEat(c: FarmCtx, itemId: string) {
  try {
    const r = await dispatchAction('food.eat', (id) => foodApi.eat(itemId, id), { key: itemId });
    if (r.poisoned && r.poison) {
      c.onPoison?.(r.poison);
      toast(`Ugh — food poisoning! -${r.poison.hpPerSec}/s for ${r.poison.durationSec}s`, 'error');
    } else {
      toast(`Ate ${foodName(itemId)} · +${r.healed} HP${r.energy ? ` +${r.energy}⚡` : ''}`);
    }
    // Health/energy changed and the bag lost a stack — refresh the panel + chrome.
    await refreshAfter(c);
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not eat', 'error');
  }
}

async function doCook(c: FarmCtx, itemId: string) {
  try {
    const st = await dispatchAction('food.cook', (id) => foodApi.cook(itemId, 1, id), { key: itemId });
    toast(`Cooking ${label(itemId)}…`);
    const bag = await fieldApi.bag();
    renderFood(c, bag, st);
    refreshBag(c);                 // cooking consumed ingredients — the hotbar must be told
    await c.game.refreshHud();     // …and so must the chrome, which reads the same counts
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not cook', 'error');
  }
}

async function doCollect(c: FarmCtx) {
  try {
    const r = await dispatchAction('food.collect', (id) => foodApi.collect(id));
    const n = Object.values(r.collected).reduce((a, b) => a + b, 0);
    // A full bag stops the collect early but banks whatever fitted first, so this
    // is a normal 200 with a flag — it used to be a throw, which lost the
    // {collected} body and skipped refreshAfter(), leaving meals that were already
    // in the bag still drawn on the fire.
    if (r.bagFull) toast(n ? `Collected ${n} cooked — ${BAG_FULL_MESSAGE}` : BAG_FULL_MESSAGE, 'error');
    else toast(n ? `Collected ${n} cooked` : 'Nothing ready yet');
    await refreshAfter(c);
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not collect', 'error');
  }
}

async function doBuildCampfire(c: FarmCtx) {
  const at = spawnAdjacentTile(c);
  try {
    const st = await dispatchAction('food.campfire', (id) => foodApi.buildCampfire(at.tx, at.ty, id));
    toast('Campfire built 🔥');
    // Place the prop now (createCampfire handles frames + entity layer).
    if (!c.campfire && st.campfire && c.entityLayer) {
      c.campfire = await createCampfire(st.campfire.tx, st.campfire.ty);
      c.entityLayer.addChild(c.campfire.sprite);
      c.animWater.push(c.campfire.anim);
      armCampfireClick(c, c.campfire.sprite);
    }
    const bag = await fieldApi.bag();
    c.bagCache = bag;
    renderFood(c, bag, st);
    refreshBag(c);                 // the campfire cost wood/stone — repaint the hotbar too
    // No `.catch(() => undefined)` here (the cook path never had one either): the
    // campfire has already been paid for server-side, so a failed HUD refresh means
    // the chrome is showing pre-build counts. Let it fall to the catch below and say
    // so — a silently stale HUD is the exact bug this pass exists to remove.
    await c.game.refreshHud();
  } catch (e) {
    if (isDropped(e)) return;
    toast(e instanceof ApiError ? e.message : 'Could not build campfire', 'error');
  }
}

/** After any food mutation: repull bag + status and re-render, and refresh HUD. */
async function refreshAfter(c: FarmCtx) {
  const [bag, st] = await Promise.all([fieldApi.bag(), foodApi.status()]);
  c.bagCache = bag;
  renderFood(c, bag, st);
  refreshBag(c);                   // repaints the hotbar off the same cache
  await c.game.refreshHud().catch(() => undefined);
}
